import React, { useState, useEffect, useMemo } from "react";
import toast, { Toaster } from "react-hot-toast";
import GenericToaster from "./theme/create_toast/GenericToaster";
import ResponsiveDrawer from "./router/ResponsiveDrawer";
import AppRoutes from "./router/AppRoutes";
import AppDataInitializer from "./components/AppDataInitializer";
import AuthzReadyGate from "./components/AuthzReadyGate";
import Keycloak from "keycloak-js";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { InputBase } from "@mui/material";
import Store from "./Redux/store";
import { useDispatch, useSelector } from "react-redux";
import KeycloackForm from "./components/KeycloackForm";
import helpers from "./UtilitiesFunctions";
import CustomLogin from "./components/CustomLogin";
import { resolveUserRole } from "./utilities/userRoleUtils";
import {
  buildKeycloakClientId,
  isCompleteLaunchContext,
  launchContextsEqual,
  normalizeLaunchContext,
  resolveLaunchContext,
  syncLaunchContextToUrl,
  writeLaunchContextToSession,
} from "./utilities/appSessionKey";
import LaunchContextSync from "./components/routing/LaunchContextSync";

// Styled components using MUI v6 styled API
const Search = styled("div")(({ theme }) => ({
  color: "white",
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.87),
    color: "black",
  },
  marginRight: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(25),
    width: "50rem",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "20ch",
    },
  },
}));

const LoginContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  padding: theme.spacing(2),
}));

/*
  App Component Refactored to Functional Component for MUI v6 & React 18 Compatibility
*/
function App() {
  const dispatch = useDispatch();
  const theme = useTheme();

  // Redux Selectors
  const cart = useSelector((state) => state.cart);
  const global_config = useSelector((state) => state.globalState);
  const user_role = useSelector((state) => state.usersState.userRole);
  const reduxToken = useSelector((state) => state.usersState.token);
  const isAuthenticated = useSelector((state) => state.authReducer.isAuthenticated);
  const authUser = useSelector((state) => state.authReducer.user);
  const authToken = useSelector((state) => state.authReducer.token);

  const resolvedToken = useMemo(() => {
    const fromUsers = Array.isArray(reduxToken) ? reduxToken[0] : reduxToken;
    if (fromUsers && !(Array.isArray(fromUsers) && fromUsers.length === 0)) {
      return fromUsers;
    }
    return authToken || "";
  }, [reduxToken, authToken]);

  const hasPersistedSession = Boolean(isAuthenticated && resolvedToken);

  // Local State — hydrate from persisted Redux so refresh does not force login
  const [keycloak, setKeycloak] = useState(null);
  const [authenticated, setAuthenticated] = useState(hasPersistedSession);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(authUser || "");
  const [token, setToken] = useState(resolvedToken || "");
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [showLogin, setShowLogin] = useState(!hasPersistedSession);
  const [loginCredentials, setLoginCredentials] = useState({});

  // Keep local auth flags aligned with persisted Redux session
  useEffect(() => {
    if (isAuthenticated && resolvedToken) {
      setShowLogin(false);
      setAuthenticated(true);
      setToken(resolvedToken);
      if (authUser) setUserInfo(authUser);
    }
  }, [isAuthenticated, resolvedToken, authUser]);

  // Setup Keycloak instance — clientId must be {appType}-{instanceId}
  const setupKeycloak = (launchCtx = resolveLaunchContext()) => {
    const ctx = normalizeLaunchContext({
      companyName: launchCtx.companyName || global_config.companyName,
      instanceId: launchCtx.instanceId || global_config.instanceId,
      appType: launchCtx.appType || global_config.appType,
    });

    // Keep legacy trailing-dash form in Redux for older startsWith checks / configs
    if (ctx.appType) {
      dispatch({
        type: "GET_BASE_URL",
        payload: {
          appType: `${ctx.appType}-`,
          instanceId: ctx.instanceId || undefined,
          companyName: ctx.companyName || undefined,
        },
      });
    }

    const clientId = buildKeycloakClientId(ctx.appType, ctx.instanceId);

    const newKeycloak = Keycloak({
      realm: ctx.companyName || global_config.companyName,
      url: global_config.keyCloakAuth,
      "ssl-required": "external",
      resource: clientId,
      clientId,
      "public-client": true,
      "verify-token-audience": true,
      "use-resource-role-mappings": true,
      "confidential-port": 0,
    });

    newKeycloak.resourceAccess = newKeycloak.resourceAccess || {};
    newKeycloak.resourceAccess.instance = ctx.appType ? `${ctx.appType}-` : "";
    setKeycloak(newKeycloak);
    return newKeycloak;
  };

  const applyLaunchContextToStore = (ctx) => {
    const normalized = normalizeLaunchContext(ctx);
    if (!isCompleteLaunchContext(normalized)) return;
    writeLaunchContextToSession(normalized);
    syncLaunchContextToUrl(normalized);
    dispatch({
      type: "GET_BASE_URL",
      payload: {
        companyName: normalized.companyName,
        instanceId: normalized.instanceId,
        // Persist legacy trailing-dash form used across menu / role checks
        appType: normalized.appType ? `${normalized.appType}-` : normalized.appType,
      },
    });
  };

  // Handle successful login
  const handleLoginSuccess = (loginData) => {
    console.log("Login successful with data:", loginData);
    const accessToken =
      loginData.responseData?.access_token ||
      loginData.responseData?.token ||
      loginData.token ||
      null;

    const user = loginData.responseData?.user || {};
    const role = resolveUserRole(user, loginData.clientId);

    if (accessToken) {
      console.log("Using token authentication");
      dispatch({
        type: "GET_TOKEN",
        token: accessToken,
      });

      if (role) {
        dispatch({
          type: "SET_USER_ROLE",
          userRole: role,
        });
      }

      setLoginCredentials(loginData);
      setShowLogin(false);
      setAuthenticated(true);
      setToken(accessToken);
      setUserInfo(user);
      setKeycloak(null);
      syncLaunchContextToUrl(resolveLaunchContext());

      console.log("Authentication state updated");
    } else {
      console.log("No token in response, showing error");
      toast.error("Authentication failed. No token received.");
    }
  };

  // Initialization — URL wins; sessionStorage keeps same-tab refresh alive.
  useEffect(() => {
    const initialize = async () => {
      const launchCtx = resolveLaunchContext();

      if (isCompleteLaunchContext(launchCtx)) {
        const state = Store.getState();
        const persistedCtx = normalizeLaunchContext({
          companyName: state?.globalState?.companyName,
          instanceId: state?.globalState?.instanceId,
          appType: state?.globalState?.appType,
        });

        const contextChanged =
          isCompleteLaunchContext(persistedCtx) &&
          !launchContextsEqual(persistedCtx, launchCtx);

        // Same appType, different company/instance (or different app):
        // drop this tab's auth + business cache. Other tabs' persist buckets stay intact.
        if (contextChanged) {
          helpers.clearData();
          dispatch({ type: "USER_LOGOUT" });
          setShowLogin(true);
          setAuthenticated(false);
          setUserInfo("");
          setToken("");
          setKeycloak(null);
        }

        applyLaunchContextToStore(launchCtx);
      }

      setupKeycloak(launchCtx);
    };

    initialize();

    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const logout = () => {
    // Clear auth for THIS tenant bucket only. Keep launch context so the
    // login screen still knows company/app/instance (no re-paste of URL).
    const launchCtx = resolveLaunchContext();
    helpers.clearData();
    dispatch({ type: "USER_LOGOUT" });
    if (isCompleteLaunchContext(launchCtx)) {
      applyLaunchContextToStore(launchCtx);
    }
    setShowLogin(true);
    setAuthenticated(false);
    setUserInfo("");
    setToken("");
    setKeycloak(null);
  };

  // Render — resolved launch context (URL > sessionStorage > Redux)
  const launchCtx = resolveLaunchContext();
  const effectiveCompanyName =
    launchCtx.companyName || global_config.companyName || "";
  const effectiveAppType = launchCtx.appType || global_config.appType || "";
  const effectiveInstanceId =
    launchCtx.instanceId || global_config.instanceId || "";

  if (!effectiveCompanyName) {
    return <KeycloackForm keycloak={keycloak} />;
  }

  if (showLogin) {
    return (
      <LoginContainer>
        <LaunchContextSync />
        <CustomLogin
          onLoginSuccess={handleLoginSuccess}
          companyName={effectiveCompanyName}
          appType={effectiveAppType}
          instanceId={effectiveInstanceId}
        />
      </LoginContainer>
    );
  }

  if (!authenticated) {
    return (
      <div className="blobcontainer">
        <div className="shapes"></div>
      </div>
    );
  }

  return (
    <>
      <LaunchContextSync />
      <Toaster
        position="bottom-right"
        reverseOrder={false}
        toastOptions={{
          className: "",
          style: {
            boxShadow: "0 0 10px 0 rgba(0,0,0, 0.4)",
            fontWeight: "bold",
          },
        }}
      />
      <GenericToaster />
      <AuthzReadyGate
        authenticated={isAuthenticated}
        appType={effectiveAppType || global_config.appType}
        onLogout={logout}
      >
        <AppDataInitializer
          authenticated={isAuthenticated}
          token={resolvedToken}
          appType={effectiveAppType || global_config.appType}
        />
        <ResponsiveDrawer
          userInfo={userInfo}
          token={resolvedToken}
          cartlength={cart?.length}
          appType={effectiveAppType || global_config.appType}
          bussinesType={global_config.bussinessType}
          onLogout={logout}
          authenticated={isAuthenticated}
          userRole={user_role}
        >
          <AppRoutes
            authenticated={isAuthenticated}
            userRole={user_role}
            appType={effectiveAppType || global_config.appType}
            onLogout={logout}
          />
        </ResponsiveDrawer>
      </AuthzReadyGate>
    </>
  );
}

export default App;
